// Imports ------------------------------------------------------------------->

import { Component, attr } from '../Component'
import { element } from '../CustomElement'
import { i18n } from '../../i18n/I18n'
import * as array from '../../utils/array'
import { FetchError } from '../../utils/fetch'

import { Link } from '../flipp-link/Link'
import { LoyaltyCardInputElement } from '../flipp-loyalty-card/LoyaltyCardInputElement'

import { inject } from '../../services/di'
import { AnalyticsService } from '../../services/AnalyticsService'
import { AuthenticationService } from '../../services/AuthenticationService'
import { AuthStatus } from '../../services/definitions/IAuthenticationService'
import { CouponService } from '../../services/CouponService'
import { LoyaltyProgramService } from '../../services/LoyaltyProgramService'
import { LocationService } from '../../services/LocationService'
import { PostcardService } from '../../services/PostcardService'
import { EventService } from '../../services/EventService'
import { UserAgentService } from '../../services/UserAgentService'
import { ToastService } from '../../services/ToastService'
import {
    LoyaltyProgramMerchant,
    LoyaltyProgram,
    LoyaltyProgramCoupon,
    LoyaltyCard,
    ToastLevel,
} from '../../definitions/CommonDefinitions'
import {
    Coupon,
    CouponRedemptionStatus,
    ClippedCoupon,
} from '../../definitions/CouponDefinitions'
import {
    CouponActions,
    CouponSource,
    LoyaltyCardSource,
} from '../../services/definitions/IAnalyticsService'
import { LinkService } from '../../services/LinkService'

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

interface CouponClipProps extends JSX.HTMLProps {
    couponId: number
    adding?: boolean
    scopedProgramId?: number
    flyerContext?: number
}

interface CouponClipModel {
    coupon: Coupon
    clippedProgramIds: number[]
    programMerchants: LoyaltyProgramMerchant[]
    loyaltyPrograms: LoyaltyProgram[]
    loyaltyCoupons: LoyaltyProgramCoupon[]
    loyaltyCards: LoyaltyCard[]
    scopedPrograms: LoyaltyProgram[]
}

@element('flipp-coupon-clipper')
export class CouponClipElement extends Component<
    CouponClipProps,
    CouponClipModel
> {
    @attr couponId: number
    @attr adding: boolean
    @attr scopedProgramId: number
    @attr flyerContext: number

    @inject auth: AuthenticationService
    @inject loyaltyService: LoyaltyProgramService
    @inject couponService: CouponService
    @inject postcardService: PostcardService
    @inject eventService: EventService
    @inject location: LocationService
    @inject userAgent: UserAgentService
    @inject analytics: AnalyticsService
    @inject toast: ToastService

    state: {
        addingProgramId: number
        unavailableMsg: string
    }

    onAttach() {
        this.addEventListener('flipp-loyaltycards', this.handleLoyaltyCard_)
        window.addEventListener('flipp-auth', this._handleAuthEvent)
    }

    onDetach() {
        this.removeEventListener('flipp-loyaltycards', this.handleLoyaltyCard_)
        window.addEventListener('flipp-auth', this._handleAuthEvent)
    }

    prepare() {
        let clippedCouponsFn = Promise.resolve<ClippedCoupon[]>([])
        let loyaltyCardsFn = Promise.resolve<LoyaltyCard[]>([])
        if (this.auth.status === AuthStatus.LOGGED_IN) {
            clippedCouponsFn = this.couponService.getClippedCoupons(
                this.auth.user
            )
            loyaltyCardsFn = this.loyaltyService.getLoyaltyCards(this.auth.user)
        }
        return Promise.all([
            this.couponService.getCoupon(this.couponId),
            this.loyaltyService.getLoyaltyPrograms(),
            this.couponService.getLoyaltyProgramCoupons(),
            this.loyaltyService.getLoyaltyProgramMerchants(),
            clippedCouponsFn,
            loyaltyCardsFn,
        ]).then(
            ([
                coupon,
                programs,
                loyaltyCoupons,
                programMerchants,
                clipped,
                loyaltyCards,
            ]) => {
                /* List of loyalty program ids that this coupon have been clipped to*/
                const loyalyCardsIds = loyaltyCards.map(
                    (card) => card.loyaltyProgramId
                )
                const clippedProgramIds = clipped
                    .filter(
                        (clippedCoupon) =>
                            clippedCoupon.program.couponId === this.couponId &&
                            clippedCoupon.status ===
                                CouponRedemptionStatus.CLIPPED &&
                            loyalyCardsIds.indexOf(
                                clippedCoupon.program.loyaltyProgramId
                            ) !== -1
                    )
                    .map(
                        (clippedCoupon) =>
                            clippedCoupon.program.loyaltyProgramId
                    )

                /* List of loyalty program coupons that use the current coupon id */
                const matchingLoyaltyCoupons = loyaltyCoupons.filter(
                    (coupon) => coupon.couponId === this.couponId
                )

                /* List of all potential loyalty programs using this coupon (clipped and unclipped) */
                const programIds = matchingLoyaltyCoupons.map(
                    (coupon) => coupon.loyaltyProgramId
                )
                const applicablePrograms = programs.filter(
                    (program) => programIds.indexOf(program.id) > -1
                )

                const scopedPrograms = this.scopedProgramId
                    ? applicablePrograms.filter(
                          (program) => program.id === this.scopedProgramId
                      )
                    : applicablePrograms
                return {
                    coupon,
                    clippedProgramIds,
                    programMerchants,
                    loyaltyPrograms: applicablePrograms,
                    loyaltyCoupons: matchingLoyaltyCoupons,
                    loyaltyCards,
                    scopedPrograms,
                }
            }
        )
    }

    render(model: CouponClipModel) {
        const isBlocked = this.userAgent.mobile && screen.width <= 600

        const isClipped = model.clippedProgramIds.length > 0
        let addingProgramName: string
        if (this.state.addingProgramId) {
            const program = array.find(
                model.loyaltyPrograms,
                (program) => program.id === this.state.addingProgramId
            )
            addingProgramName = program.name
        }

        let helpUrl = `https://share.flipp.com/help?locale=${this.location.locale}&postal_code=${this.location.postalCode}&coupon_id=${this.couponId}`
        if (this.scopedProgramId) {
            helpUrl += `&loyalty_program=${this.scopedProgramId}`
        }

        return (
            <div class="card-listing" classList={{ clipped: isClipped }}>
                <h3>{i18n('clip_cta')}</h3>
                <div flex-horizontal>
                    {model.scopedPrograms.map((program) => {
                        if (isBlocked) {
                            return (
                                <Link
                                    to="install"
                                    params={{ feature: 'coupons' }}
                                >
                                    <img src={program.cardImageUrl} alt="" />
                                </Link>
                            )
                        } else {
                            return (
                                <button
                                    id={`coupon-${this.model.coupon.id}-clip-button-${program.id}`}
                                    classList={{
                                        clipped:
                                            model.clippedProgramIds.indexOf(
                                                program.id
                                            ) > -1,
                                    }}
                                    onClick={this.handleCouponClip_.bind(
                                        this,
                                        program.id,
                                        CouponActions.Add
                                    )}
                                >
                                    <div class="marker">
                                        <img
                                            class="icon"
                                            src="https://f.wishabi.net/creative/Distribution/images/icons/clippedMarker.svg"
                                            alt=""
                                        />
                                    </div>
                                    <img src={program.cardImageUrl} alt="" />
                                </button>
                            )
                        }
                    })}
                </div>

                {this.state.unavailableMsg ? (
                    <div class="alert error" flex-horizontal>
                        <img
                            class="icon"
                            src="https://f.wishabi.net/creative/Distribution/images/icons/alert.svg"
                            alt=""
                        />
                        <div>{this.state.unavailableMsg}</div>
                    </div>
                ) : this.adding && this.auth.status !== AuthStatus.LOGGED_IN ? (
                    <div class="login" flex-vertical>
                        <div class="alert" flex-horizontal>
                            <img
                                class="icon"
                                src="https://f.wishabi.net/creative/Distribution/images/icons/alert.svg"
                                alt=""
                            />
                            <div>{i18n('add_card_login_alert')}</div>
                        </div>

                        <div flex-horizontal>
                            <div class="button-area" flex-horizontal>
                                <button
                                    class="authenticate"
                                    onClick={this.handleSignIn}
                                >
                                    <div>
                                        <span>{i18n('signin')}</span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                ) : this.adding ? (
                    <div flex-vertical>
                        <div class="login" flex-vertical>
                            <div class="alert" flex-horizontal>
                                <img
                                    class="icon"
                                    src="https://f.wishabi.net/creative/Distribution/images/icons/alert.svg"
                                    alt=""
                                />
                                <div>
                                    {i18n('add_card_alert', {
                                        name: addingProgramName,
                                    })}
                                </div>
                            </div>
                        </div>

                        <LoyaltyCardInputElement
                            programId={this.state.addingProgramId}
                            editing={true}
                            cancelable={false}
                            context={
                                this.scopedProgramId
                                    ? LoyaltyCardSource.FlyerItem
                                    : LoyaltyCardSource.CouponDetails
                            }
                        />
                    </div>
                ) : null}

                <div class="help" flex-horizontal>
                    <div class="icon" flex-horizontal>
                        <span>?</span>
                    </div>
                    <a class="message" href={helpUrl} target="_blank">
                        {i18n('clipping_help_msg')}
                    </a>
                </div>
            </div>
        )
    }

    clip() {
        const programs = this.model.scopedPrograms
        programs.forEach((program) => {
            if (this.model.clippedProgramIds.indexOf(program.id) === -1) {
                this.handleCouponClip_(program.id, CouponActions.AddMatched)
            }
        })
    }
    private handleSignIn = (e: Event) => {
        e.preventDefault()
        const linkService = LinkService.getInstance()
        linkService.execute('/signin', this)
    }

    private handleCouponClip_ = (
        programId: number,
        analyticsAction: CouponActions,
        evt?: Event
    ) => {
        if (evt) {
            evt.stopPropagation()
            evt.preventDefault()
        }

        this.state.unavailableMsg = null

        const loyaltyProgramCoupon = array.find(
            this.model.loyaltyCoupons,
            (coupon) => programId === coupon.loyaltyProgramId
        )
        const loyaltyCard = array.find(
            this.model.loyaltyCards,
            (card) => programId === card.loyaltyProgramId
        )
        const programMerchant = array.find(
            this.model.programMerchants,
            (merchant) =>
                merchant.loyaltyProgramId ===
                loyaltyProgramCoupon.loyaltyProgramId
        )

        if (
            this.adding &&
            this.state.addingProgramId !== programId &&
            !loyaltyCard
        ) {
            this.state.addingProgramId = programId
            this.invalidate()
            return
        } else if (!loyaltyCard) {
            this.state.addingProgramId = programId
            this.adding = true
            return
        }

        /* getElement* usage should be avoided here, but it's necessary because we render a list of buttons, and
         * we can't currently maintain a ref to each one individually
         * FIXME: [FW-XXX]: Investigate approach for using array for component refs */
        const button = document.getElementById(
            `coupon-${this.model.coupon.id}-clip-button-${programId}`
        )
        if (button.classList.contains('processing')) {
            // Coupon already being clipped - return
            return
        } else if (this.model.clippedProgramIds.indexOf(programId) > -1) {
            // Coupon already clipped - return
            return
        }
        /* Add a class directly on the element. Yes, this breaks our component model, but a complete re-render
         * is overkill here. This can be revisited once we add virtual DOM
         * FIXME: [FW-XXX]: */
        button.classList.add('processing')

        if (!loyaltyCard.registered) {
            this.state.unavailableMsg = i18n('unavailable_message', {
                card: loyaltyCard.name,
            })
            this.invalidate()
            return
        }

        this.couponService
            .clipCoupon(
                this.auth.user.token.user_id,
                loyaltyCard,
                loyaltyProgramCoupon
            )
            .then(
                () => {
                    this.couponService.clearClippedCoupons()
                    this.invalidate()
                    button.classList.remove('processing')
                    this.eventService.fireCouponClippedEvent(
                        this.couponId,
                        'clip-element',
                        'added',
                        button
                    )

                    /* Track the coupon clip event */
                    const context = this.scopedProgramId
                        ? CouponSource.ItemDetails
                        : CouponSource.CouponDetails
                    this.analytics.trackCoupon(
                        this.model.coupon,
                        analyticsAction,
                        context,
                        this.flyerContext,
                        programMerchant,
                        loyaltyProgramCoupon
                    )
                },
                (err: Error) => {
                    let errorMsg: string
                    if (err instanceof FetchError) {
                        const body = JSON.parse(err.body)
                        const msg = this.postcardService.getErrorMessage(
                            body.error_code
                        )
                        errorMsg = `${i18n('clipping_error')} ${msg}`
                    } else {
                        errorMsg = i18n('clipping_error')
                    }
                    this.toast.fireToastEvent(
                        errorMsg,
                        ToastLevel.ERROR,
                        false,
                        button
                    )
                    button.classList.remove('processing')
                }
            )
    }

    private handleLoyaltyCard_ = (e: CustomEvent) => {
        e.preventDefault()
        this.adding = false
    }

    private _handleAuthEvent = () => {
        this.adding = false
    }
}
